import { keyframes } from '@emotion/core';
import { Box } from '@modusign/design-system';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import LoadingStatus from '@libs/loading-status';

import { useColorScheme } from '@app/hooks/useColorScheme';
import useUsageCount from '@app/hooks/useUsageCount';
import {
  getPermissionsAndLimitsLoadingStatus,
  getUserAuthorityLoadingStatus,
} from '@app/store/user/authority/selectors';

import { REMAINING_USAGE_DURATION } from '..';

const GNBUsageGauge = memo(() => {
  const { colorScheme } = useColorScheme();
  const { signingRequestCount } = useUsageCount();

  const userAuthorityLoadingStatus = useSelector(getUserAuthorityLoadingStatus);
  const permissionsAndLimitsLoadingStatus = useSelector(getPermissionsAndLimitsLoadingStatus);

  const isLoading =
    userAuthorityLoadingStatus === LoadingStatus.PENDING ||
    permissionsAndLimitsLoadingStatus === LoadingStatus.PENDING;

  const gaugePercentage = useMemo(() => {
    if (signingRequestCount.limit === 0) {
      return 0;
    }
    if (signingRequestCount.limit === Infinity) {
      return 100;
    }
    return (
      ((signingRequestCount.limit - signingRequestCount.usage) / signingRequestCount.limit) * 100
    );
  }, [signingRequestCount]);

  const GAUGE_MIN_WIDTH = signingRequestCount.limit - signingRequestCount.usage && '8px';

  const stretch = keyframes`
    0% {
        width: ${GAUGE_MIN_WIDTH}
      }
    100% {
        width: ${gaugePercentage}%
      }`;

  return (
    <Box
      bg="statusBackgroundNeutralBase"
      borderRadius="2px"
      flex="1 0 auto"
      height="2px"
      overflowX="hidden"
    >
      <Box
        bg={
          // eslint-disable-next-line no-nested-ternary
          isLoading
            ? 'statusBackgroundNeutralBase'
            : colorScheme === 'default'
            ? 'iconPrimaryBase'
            : 'iconPrimaryDark'
        }
        borderRadius="2px"
        height="2px"
        sx={{
          animation: stretch,
          animationDuration: `${REMAINING_USAGE_DURATION}ms`,
          animationTimingFunction: 'ease-in-out',
          animationFillMode: 'forwards',
        }}
        width={GAUGE_MIN_WIDTH}
      />
    </Box>
  );
});

GNBUsageGauge.displayName = 'UsageGauge';

export default GNBUsageGauge;
